import { getContactsSortedByRank } from '@/services/contact-point';
import { ContactPoint, Organization } from '@medplum/fhirtypes';
import { ContactPointType } from '@/components/Timeline/types';
import { ORGANIZATION_CONTACT_TITLE_EXTENSION } from '@bariendo/lib/extensions';

export interface ContactPhoneWithName {
  phone: ContactPoint;
  contactName?: string;
  contactRole?: string;
}

export interface OrganizationPhones {
  organizationPhones: ContactPoint[];
  contactPhones: ContactPhoneWithName[];
  allPhones: ContactPoint[];
}

export interface PhoneSection {
  sectionLabel: string;
  items: Array<{
    key: string;
    label: string;
    caption?: string;
    onClick: () => void;
  }>;
}

export function getOrganizationPhones(organization?: Organization): ContactPoint[] {
  const structured = getOrganizationPhonesStructured(organization);
  return structured.allPhones;
}

export function getOrganizationPhonesStructured(organization?: Organization): OrganizationPhones {
  if (!organization) {
    return {
      organizationPhones: [],
      contactPhones: [],
      allPhones: [],
    };
  }

  // Organization Contact Phones
  const organizationPhones = getContactsSortedByRank(
    organization.telecom?.filter((cp) => cp.system === ContactPointType.PHONE),
    ContactPointType.PHONE
  );

  // Contact Phones with Names
  const contactPhonesWithNames: ContactPhoneWithName[] =
    organization.contact?.flatMap((contact) => {
      const phones = contact.telecom?.filter((cp) => cp.system === ContactPointType.PHONE) ?? [];

      // Construct full name from given and family names
      const givenName = contact.name?.given?.[0] || '';
      const familyName = contact.name?.family || '';
      const contactName = [givenName, familyName].filter(Boolean).join(' ');

      // Get title from extension
      const contactRole = contact.extension?.find((e) => e.url === ORGANIZATION_CONTACT_TITLE_EXTENSION)?.valueString;

      return phones.map((phone) => ({
        phone,
        contactName,
        contactRole,
      }));
    }) ?? [];

  // Sort contact phones by rank
  const sortedContactPhones = contactPhonesWithNames.sort((a, b) => {
    const rankA = a.phone.rank ?? Infinity;
    const rankB = b.phone.rank ?? Infinity;
    return rankA - rankB;
  });

  const allContactPhones = sortedContactPhones.map((cp) => cp.phone);
  const allPhones = getContactsSortedByRank([...organizationPhones, ...allContactPhones], ContactPointType.PHONE);

  return {
    organizationPhones,
    contactPhones: sortedContactPhones,
    allPhones,
  };
}

/**
 * Creates phone sections for dropdown menus from organization phone data
 * @param phonesData - The structured phone data from getOrganizationPhonesStructured
 * @param onOrganizationPhoneClick - Callback for when an organization phone is clicked
 * @param onContactPhoneClick - Callback for when a contact phone is clicked (optional)
 * @param includeContactPhones - Whether to include the contact phones section (defaults to true)
 * @returns Array of phone sections with labels and items for dropdown menus
 */
export function createPhoneSections(
  phonesData: OrganizationPhones | null,
  onOrganizationPhoneClick: (phone: ContactPoint) => void,
  onContactPhoneClick?: (phone: ContactPoint) => void,
  includeContactPhones: boolean = true
): PhoneSection[] {
  if (!phonesData) return [];

  const sections: PhoneSection[] = [];

  // Organization phones section
  if (phonesData.organizationPhones.length > 0) {
    sections.push({
      sectionLabel: 'Contact Info',
      items: phonesData.organizationPhones.map((phone) => ({
        key: phone.value!,
        label: '',
        caption: phone.value,
        onClick: () => onOrganizationPhoneClick(phone),
      })),
    });
  }

  // Contact phones section (optional)
  if (includeContactPhones && onContactPhoneClick && phonesData.contactPhones.length > 0) {
    sections.push({
      sectionLabel: 'Contacts',
      items: phonesData.contactPhones.map((contactPhone) => {
        const namePart = contactPhone.contactName || '';
        const rolePart = contactPhone.contactRole || '';
        const label = namePart && rolePart ? `${namePart} - ${rolePart}` : namePart || rolePart;

        return {
          key: contactPhone.phone.value!,
          label,
          caption: contactPhone.phone.value,
          onClick: () => onContactPhoneClick(contactPhone.phone),
        };
      }),
    });
  }

  return sections;
}
